import React, { useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

type SortableContextProps = {
  children: React.ReactNode;
  items: any[];
  onReorder?: (newItems: any[]) => void;
  idField: string;
  tableId: string; };

export const SortableTableProvider: React.FC<SortableContextProps> = ({
  children,
  items,
  onReorder,
  idField,
  tableId,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const activeItemId = active.id.toString().replace(`${tableId}-`, "");
        const overItemId = over.id.toString().replace(`${tableId}-`, "");

        const oldIndex = items.findIndex((item) => item[idField] === activeItemId);
        const newIndex = items.findIndex((item) => item[idField] === overItemId);

        if (oldIndex !== -1 && newIndex !== -1) {
          const newItems = arrayMove(items, oldIndex, newIndex);
          if (onReorder) {
            onReorder(newItems);
          }
        }
      }
    },
    [items, onReorder, idField, tableId],
  );

  return (
    <DndContext
      id={tableId}
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}>
      <SortableContext
        items={items.map((item) => `${tableId}-${item[idField]}`)}
        strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
};
